/**
 * Small, pure display formatters used across the detail views, kept here so their rounding rules are
 * consistent and unit-testable (locale-dependent timestamp rendering is deliberately kept trivial
 * and defensive rather than asserted against a specific locale).
 */

/** Format a millisecond duration as a rounded integer with a unit, e.g. `123 ms`. */
export function formatMs(ms: number): string {
  if (!Number.isFinite(ms)) return '—';
  return `${Math.round(ms)} ms`;
}

/**
 * Format a percentage (0–100) with up to two decimals, trimming trailing zeros so whole values read
 * as `75%` and fractional ones as `66.67%`.
 */
export function formatPercent(pct: number): string {
  if (!Number.isFinite(pct)) return '—';
  const rounded = Math.round(pct * 100) / 100;
  return `${rounded}%`;
}

/**
 * Render an ISO-8601 timestamp using the runtime locale, falling back to the raw string when it is
 * not a parseable date (so a malformed value is shown verbatim rather than as `Invalid Date`).
 */
export function formatTimestamp(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  return new Date(ms).toLocaleString();
}

/**
 * Render an **epoch-seconds** instant using the runtime locale. The generation feature's DTOs carry
 * times as epoch seconds rather than ISO strings (`createdAtEpoch`, `expiresAtEpoch`), so the
 * seconds→milliseconds conversion lives here once instead of at each call site. A non-finite value
 * reads as `—`, matching {@link formatMs} and {@link formatPercent}.
 */
export function formatEpochSeconds(epochSeconds: number): string {
  if (!Number.isFinite(epochSeconds)) return '—';
  return new Date(epochSeconds * 1000).toLocaleString();
}
